import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, Table2, Search, ListChecks, FileText } from "lucide-react";
import { useApi, fetchProjects } from "../api";
import { StatusPill, PriorityPill, ProgressBar, Avatar, PageHeader, Loading, ErrorState, statusColor, formatDate } from "../components/common";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

function ProjectCard({ p, onClick }) {
  return (
    <div onClick={onClick} className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-red-600">{p.code}</p>
          <h3 className="mt-0.5 truncate text-base font-bold text-slate-900">{p.name}</h3>
        </div>
        <PriorityPill value={p.priority} />
      </div>
      {p.description && <p className="mt-2 line-clamp-2 text-sm text-slate-500">{p.description}</p>}
      <div className="mt-3"><StatusPill value={p.status} /></div>
      <div className="mt-4">
        <div className="mb-1 flex justify-between text-xs font-medium text-slate-500"><span>Progress</span><span>{p.progress}%</span></div>
        <ProgressBar value={p.progress} />
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {p.tasksTotal}</span>
          <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {p.docsCount}</span>
        </div>
        <Avatar avatar={p.pmAvatar} size={28} />
      </div>
    </div>
  );
}

function KanbanView({ list, statuses, onOpen }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {statuses.map((col) => {
        const items = list.filter((p) => p.status === col);
        return (
          <div key={col} className="rounded-2xl bg-slate-100/70 p-3">
            <div className="mb-3 flex items-center gap-2 px-1">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: statusColor(col).dot }} />
              <h3 className="text-sm font-bold text-slate-700">{col}</h3>
              <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-500">{items.length}</span>
            </div>
            <div className="space-y-3">
              {items.map((p) => <ProjectCard key={p.id} p={p} onClick={() => onOpen(p.id)} />)}
              {items.length === 0 && <p className="px-1 py-6 text-center text-xs text-slate-400">No projects</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TableView({ list, onOpen }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Project</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3 w-40">Progress</th><th className="px-4 py-3">Tasks</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">PM</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((p) => (
              <tr key={p.id} onClick={() => onOpen(p.id)} className="cursor-pointer transition-colors hover:bg-slate-50">
                <td className="px-4 py-3"><p className="font-semibold text-slate-900">{p.name}</p><p className="text-xs text-slate-400">{p.code}</p></td>
                <td className="px-4 py-3"><StatusPill value={p.status} /></td>
                <td className="px-4 py-3"><PriorityPill value={p.priority} /></td>
                <td className="px-4 py-3"><div className="flex items-center gap-2"><ProgressBar value={p.progress} /><span className="text-xs font-medium text-slate-500">{p.progress}%</span></div></td>
                <td className="px-4 py-3 text-slate-600">{p.tasksTotal}</td>
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">{formatDate(p.dueDate)}</td>
                <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar avatar={p.pmAvatar} size={26} /><span className="hidden text-slate-600 lg:inline">{p.pm}</span></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const { user } = useAuth();
  const { data: projects, loading, error, reload } = useApi(fetchProjects);
  const open = (id) => navigate(`/projects/${id}`);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading || !projects) return <Loading />;

  const statuses = [...new Set(projects.map((p) => p.status))];
  const mineCount = projects.filter((p) => p.pmId === user?.id || p.pm === user?.name).length;
  const list = projects.filter((p) => {
    const matchQ = (p.name + p.code + p.customer + p.pm).toLowerCase().includes(q.toLowerCase());
    const matchMine = !mine || p.pmId === user?.id || p.pm === user?.name;
    return matchQ && matchMine;
  });

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Projects" subtitle={`${projects.length} live projects`} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects..." className="h-9 border-slate-200 bg-white pl-9 text-sm" />
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button onClick={() => setMine(false)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${!mine ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>All projects</button>
          <button onClick={() => setMine(true)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${mine ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>My projects ({mineCount})</button>
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button onClick={() => setView("kanban")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${view === "kanban" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /> Kanban</button>
          <button onClick={() => setView("table")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${view === "table" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}><Table2 className="h-4 w-4" /> Table</button>
        </div>
      </div>
      {view === "kanban" ? <KanbanView list={list} statuses={statuses} onOpen={open} /> : <TableView list={list} onOpen={open} />}
    </div>
  );
}
